import { FormEvent, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { usePermissions } from '../lib/usePermissions';
import { formatDate } from '../lib/orgTimezone';

/**
 * ForceContingencyPanel — A14, the gated manual override on the billing surface.
 *
 * WHY IT LIVES HERE. The Billing tab lists what is *staged to bill* — items already
 * at `billable`. An item you would want to FORCE is by definition not there yet: it
 * is `pending` (the event hasn't happened) or `non_billable` (Y3 wrote a $0 record
 * explaining a win that produced no fee). So the very items this override exists for
 * are invisible on the one screen a biller lives on. This panel is that missing half.
 *
 * WHO SEES IT. Super admins and office administrators only — the same ROLE gate the
 * server enforces (`FORCE_ROLES`), mirrored here so the control never renders for a
 * role that would be refused. The gate is by role, not capability, because an analyst
 * genuinely holds `invoice_item.can_edit` and a capability gate would show them a
 * button that 403s.
 *
 * The FE gate is UX only; `routes/force-contingency.js` is the real boundary.
 */

/** Statuses an item can be forced FROM — mirrors FORCEABLE_STATUSES on the server. */
const FORCEABLE = new Set(['pending', 'non_billable']);
/** Roles permitted to force — mirrors FORCE_ROLES on the server. */
const FORCE_ROLES = new Set(['super_admin', 'administrator']);

export interface ForceableItem {
  id: string;
  status: string | null;
  amount: number | null;
  parcel_id: string | null;
  billing_reason: string | null;
  forced_by: string | null;
  forced_at: string | null;
  forced_reason: string | null;
}

const money = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

export function ForceContingencyPanel({ clientId }: { clientId: string }) {
  const { data: perms } = usePermissions();
  const [target, setTarget] = useState<ForceableItem | null>(null);

  const canForce = FORCE_ROLES.has(perms?.user?.role ?? '');

  // Fetched only for a role that may act — no point showing a biller a list they
  // cannot use, and no point spending the request. `enabled` also keeps the panel
  // silent (not empty-stated) for everyone else.
  const itemsQuery = useQuery<ForceableItem[]>({
    queryKey: ['forceable-items', clientId],
    queryFn: ({ signal }) => api.get<ForceableItem[]>(`/api/invoice-items?client_id=${clientId}`, signal),
    enabled: canForce && !!clientId,
  });

  if (!canForce) return null;

  const all = itemsQuery.data ?? [];
  const items = all.filter((i) => FORCEABLE.has(i.status ?? ''));
  // Already-forced items. The wireframe requires the trace be "surfaced on the
  // record", and a forced item flips to `billable` — so it LEAVES the forceable list
  // above and would otherwise sit in the ordinary billables table looking exactly
  // like a fee the engine computed. Without this block the forced_by/forced_at
  // columns would be written and never read, which is the whole failure mode this
  // feature was built to correct.
  const forced = all.filter((i) => !!i.forced_by);
  if (itemsQuery.isLoading || (items.length === 0 && forced.length === 0)) return null;

  return (
    <section style={{ marginTop: 'var(--sp-6)' }}>
      {forced.length > 0 && (
        <div style={{ marginBottom: items.length > 0 ? 'var(--sp-6)' : 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
              Forced
            </h3>
            <Badge variant="warning">{forced.length}</Badge>
          </div>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            Billed ahead of the triggering event. The amount was asserted by a person, not
            computed — so these read differently from an ordinary fee and are listed here.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--color-muted)' }}>
                  <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Forced by</th>
                  <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)' }}>When</th>
                  <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Reason</th>
                  <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)', textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {forced.map((i) => (
                  <tr key={i.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--sp-2)' }}>{i.forced_by}</td>
                    <td style={{ padding: 'var(--sp-2)' }}>{i.forced_at ? formatDate(i.forced_at) : '—'}</td>
                    <td style={{ padding: 'var(--sp-2)', color: 'var(--color-ink-3)' }}>{i.forced_reason || '—'}</td>
                    <td style={{ padding: 'var(--sp-2)', textAlign: 'right', fontWeight: 'var(--fw-semibold)' }}>
                      {money(i.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {items.length > 0 && (
      <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          Not yet billable
        </h3>
        <Badge variant="neutral">{items.length}</Badge>
      </div>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        These have not met their billing trigger. Forcing one bills it{' '}
        <strong>before the event has occurred</strong> — restricted to super admins and
        office administrators, and recorded on the item.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-muted)' }}>
              <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Status</th>
              <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Why not billable</th>
              <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)', textAlign: 'right' }}>Amount</th>
              <th scope="col" style={{ padding: 'var(--sp-1) var(--sp-2)' }} />
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: 'var(--sp-2)' }}>
                  <Badge variant={i.status === 'non_billable' ? 'warning' : 'neutral'}>
                    {(i.status ?? '').replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td style={{ padding: 'var(--sp-2)', color: 'var(--color-ink-3)' }}>
                  {i.billing_reason || '—'}
                </td>
                <td style={{ padding: 'var(--sp-2)', textAlign: 'right' }}>{money(i.amount)}</td>
                <td style={{ padding: 'var(--sp-2)', textAlign: 'right' }}>
                  <Button variant="secondary" size="sm" onClick={() => setTarget(i)}>
                    Force contingency
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </>
      )}

      <ForceContingencyModal
        item={target}
        clientId={clientId}
        onClose={() => setTarget(null)}
      />
    </section>
  );
}

function ForceContingencyModal({ item, clientId, onClose }: {
  item: ForceableItem | null; clientId: string; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/workflow/invoice-items/${item!.id}/force-contingency`, {
      amount: Number(amount),
      reason: reason.trim() || undefined,
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Contingency forced — the item is now billable.' });
      // It leaves this list and joins the staged billables, so refresh both.
      queryClient.invalidateQueries({ queryKey: ['forceable-items', clientId] });
      queryClient.invalidateQueries({ queryKey: ['billables', clientId] });
      queryClient.invalidateQueries({ queryKey: ['owner-invoice-summary', clientId] });
      setAmount(''); setReason('');
      onClose();
    },
    onError: (err) => {
      const body = err instanceof ApiClientError ? (err.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || err.message });
    },
  });

  // The server refuses a non-positive amount; mirror it so the button can't be armed
  // on a value that is guaranteed to 400.
  const parsed = Number(amount);
  const canSubmit = Number.isFinite(parsed) && parsed > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  return (
    <Modal
      open={!!item}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title="Force contingency"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit}
            onClick={() => canSubmit && mutation.mutate()}>
            Force and make billable
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          This bills <strong>before the triggering event has occurred</strong>. The amount is
          asserted rather than calculated, the item becomes billable, and your name and the
          time are recorded on it.
        </p>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>
            Forced amount
          </span>
          <Input type="number" step="0.01" min="0" value={amount} required
            onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </label>
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>
            Reason <span style={{ color: 'var(--color-muted)', fontWeight: 'var(--fw-regular)' }}>(optional)</span>
          </span>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this being billed early?" />
        </label>
      </form>
    </Modal>
  );
}
